import React, { useEffect, useState } from 'react';

import CustomerSurveyModal from './CustomerSurveyModal';
import {
  clearPendingCustomerSurveyRequestId,
  CUSTOMER_SURVEY_REQUEST_ACTIVITY_EVENT,
  readPendingCustomerSurveyRequestId,
} from './customerSurveyEvents';
import type { CustomerSurveyRequestActivityDetail } from './customerSurveyEvents';
import useCustomerSurveyPrompt from './useCustomerSurveyPrompt';

type CustomerSurveyRequestPromptProps = {
  requestId?: string | number;
  enabled?: boolean;
};

export default function CustomerSurveyRequestPrompt({
  requestId,
  enabled = true,
}: CustomerSurveyRequestPromptProps) {
  const [allowedToOpen, setAllowedToOpen] = useState(true);
  const [eventKey, setEventKey] = useState(0);
  const [eventRequestId, setEventRequestId] = useState<string | number | undefined>(requestId);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const pendingRequestId = readPendingCustomerSurveyRequestId();
    if (!pendingRequestId) return;

    clearPendingCustomerSurveyRequestId();
    setEventRequestId(pendingRequestId);
    setEventKey((current) => current + 1);
    setAllowedToOpen(false);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const onRequestActivity = (event: Event) => {
      const detail = (event as CustomEvent<CustomerSurveyRequestActivityDetail>).detail;
      clearPendingCustomerSurveyRequestId();
      setEventRequestId(detail?.requestId ?? requestId);
      setEventKey((current) => current + 1);
      setAllowedToOpen(false);
    };

    window.addEventListener(CUSTOMER_SURVEY_REQUEST_ACTIVITY_EVENT, onRequestActivity);
    return () => {
      window.removeEventListener(CUSTOMER_SURVEY_REQUEST_ACTIVITY_EVENT, onRequestActivity);
    };
  }, [requestId]);

  useEffect(() => {
    if (typeof document === 'undefined' || eventKey <= 0) return undefined;

    let cancelled = false;

    const isVisibleElement = (element: Element) => {
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);

      return (
        rect.width > 0 &&
        rect.height > 0 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden'
      );
    };

    const hasBlockingDialog = () =>
      Array.from(
        document.querySelectorAll('[role="dialog"], [aria-modal="true"], .modal, [data-modal-root]'),
      ).some(isVisibleElement);

    const waitForClearScreen = () => {
      if (cancelled) return;
      if (!hasBlockingDialog()) {
        setAllowedToOpen(true);
        return;
      }
      window.setTimeout(waitForClearScreen, 500);
    };

    waitForClearScreen();

    return () => {
      cancelled = true;
    };
  }, [eventKey]);

  const { open, config, dismiss, handleSubmit } = useCustomerSurveyPrompt({
    source: 'request',
    triggerType: 'completed_request',
    enabled: enabled && eventKey > 0 && allowedToOpen,
    requestId: eventRequestId ?? requestId,
    recheckKey: eventKey,
  });

  if (!open || !config) return null;

  return <CustomerSurveyModal config={config} onDismiss={dismiss} onSubmit={handleSubmit} />;
}
